'use client';

import { c as _c } from "react/compiler-runtime";
import React, { Fragment, useState } from 'react';
import { Menu, MenuButton, MenuItem, MenuItems, Transition } from '@headlessui/react';
import { usePathname } from 'next/navigation';
import { UserStatus } from '@/src/lib/customer/types';
import { GetUser } from '@/src/lib/auth/types';
import { Dictionary } from '@/src/lib/dictionary/types';
import { ProfileNavigationItem, useCustomerNavigation } from '@/src/lib/customer/hooks/profile-navigation-items';
import Link from 'next/link';
import { DeserializedCompanyUser } from '@/src/lib/company-user/types';
import classNames from 'classnames';
import { getDefaultCompanyBusinessUnit, getSelectedPricingReference } from '@/src/lib/company-user/utils/company-user-utils';
import UserProfileButtonLoggedOut from '@/src/app/components/Header/UserProfileButton/UserProfileButtonLoggedOut';
import { isAccountModalAvailable } from '@/src/app/components/Header/UserProfileButton/lib/account-button-helpers';
import { DeserializedCart } from '@/src/lib/cart/glue/types/deserialized';
import { isUserRegisteredOrVerified } from '@/src/lib/auth/business/user-status';
import { invalidateCompanyUserCache } from '@/src/lib/company-user/business/invalidate-company-user-cache.action';
import { eventBus } from '@/src/lib/core/event/bus';
import { EventBusName } from '@/src/lib/core/event/types';
import { useAsyncStartTransition } from '@/src/lib/utils/hooks/use-async-start-transition';
import Icon from '@/src/app/components/Icon/Icon';
import { IconName } from '@/src/app/components/Icon/types';
function getInitialsFromUserName(firstName: string, lastName: string) {
  return `${firstName.charAt(0)}${lastName.charAt(0)}`;
}
type UserProfileButtonProps = {
  user?: GetUser;
  companyUsers?: Array<DeserializedCompanyUser>;
  lang: string;
  country: string;
  dict: Dictionary;
  cart?: DeserializedCart;
  onAccountModalOpen?: typeof invalidateCompanyUserCache;
  isOrderHistoryPageEnabled?: boolean;
};
const MenuItemContent = t0 => {
  const $ = _c(7);
  const {
    item
  } = t0;
  let t1;
  if ($[0] !== item.icon) {
    t1 = item.icon !== null ? <Icon name={item.icon} className="text-base! text-gray-400 group-hover:text-gray-500" aria-hidden="true" /> : null;
    $[0] = item.icon;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== item.title) {
    t2 = <span className="ml-3">{item.title}</span>;
    $[2] = item.title;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <>{t1}{t2}</>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
};
export default function UserProfileButtonClient(t0) {
  const $ = _c(25);
  const {
    user,
    dict,
    lang,
    country,
    companyUsers,
    cart,
    onAccountModalOpen,
    isOrderHistoryPageEnabled
  } = t0;
  const pathname = usePathname();
  const {
    navigationItems
  } = useCustomerNavigation(lang, country, dict, isOrderHistoryPageEnabled);
  const [accountModalOpenLoading, setAccountModalOpenLoading] = useState(false);
  const asyncStartTransition = useAsyncStartTransition();
  let t1;
  if ($[0] !== asyncStartTransition || $[1] !== onAccountModalOpen) {
    t1 = async function onAccountModalOpenClick() {
      setAccountModalOpenLoading(true);
      if (onAccountModalOpen) {
        await asyncStartTransition(async () => {
          await onAccountModalOpen();
        });
      }
      eventBus.emit(EventBusName.HEADER_NAVIGATION_CLICK_OPEN_ACCOUNT_MODAL);
      setAccountModalOpenLoading(false);
    };
    $[0] = asyncStartTransition;
    $[1] = onAccountModalOpen;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const onAccountModalOpenClick = t1;
  if (pathname?.includes("/checkout") && !pathname?.includes("/success")) {
    return null;
  }
  if (user?.user?.customer && isUserRegisteredOrVerified(user) && companyUsers) {
    let t2;
    if ($[3] !== companyUsers) {
      t2 = getDefaultCompanyBusinessUnit(companyUsers);
      $[3] = companyUsers;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    const defaultBusinessUnit = t2;
    let t3;
    if ($[5] !== cart) {
      t3 = getSelectedPricingReference(cart);
      $[5] = cart;
      $[6] = t3;
    } else {
      t3 = $[6];
    }
    const selectedPricingReference = t3;
    let t4;
    if ($[7] !== accountModalOpenLoading || $[8] !== companyUsers || $[9] !== defaultBusinessUnit || $[10] !== dict || $[11] !== navigationItems || $[12] !== onAccountModalOpenClick || $[13] !== selectedPricingReference || $[14] !== user.user.customer.email || $[15] !== user.user.customer.firstName || $[16] !== user.user.customer.lastName) {
      t4 = <Menu as="div" className="relative text-left inline-block">{t5 => {
          const {
            open
          } = t5;
          return <><div><MenuButton data-testid="user-profile-button" className="bg-black text-white flex flex-row items-stretch no-underline! h-[3.125rem]"><span className="w-[50px] h-[50px] inline-flex justify-center items-center"><span className="text-white font-bold tracking-widest text-base" data-testid="button-initials">{getInitialsFromUserName(String(user.user.customer.firstName), String(user.user.customer.lastName))}</span></span><span className="px-3 bg-gray-600 inline-flex justify-center items-center text-sm  font-semibold" data-testid="button-text"><>{user.user.customer.firstName} {user.user.customer.lastName}</></span><span className="bg-gray-600 pt-[13px] pr-2"><Icon data-testid="expand-user-profile" name={IconName.EXPAND_MORE} className={classNames("text-primary transition-transform duration-150", {
                    "rotate-180": open
                  })} /></span></MenuButton></div>{open && <div><Transition as={Fragment} enter="transition ease-out duration-100" enterFrom="transform opacity-0 scale-95" enterTo="transform opacity-100 scale-100" leave="transition ease-in duration-75" leaveFrom="transform opacity-100 scale-100" leaveTo="transform opacity-0 scale-95" show={open}><MenuItems className="text-white absolute right-0 z-10 mt-1 w-56 origin-top-right divide-y divide-gray-800 bg-black shadow-lg ring-1 ring-black/5 focus:outline-none" data-testid="user-profile-dropdown"><div className="px-4 py-3 bg-gray-600 text-white"><p className="roofline text-[0.75rem] font-bold">{dict.general.signedInAs}</p>{defaultBusinessUnit ? <p className="text-sm mt-[5px]">{dict.userProfile.menu.account} {defaultBusinessUnit.accountNumber}</p> : null}<p className="truncate text-sm mt-[5px] text-gray-100">{user.user?.customer?.email}</p>{selectedPricingReference ? <p className="text-sm mt-[5px]">{dict.userProfile.menu.priceRef} {selectedPricingReference}</p> : null}{isAccountModalAvailable(companyUsers) ? <p className="text-sm mt-2 flex flex-row">{accountModalOpenLoading ? <Icon name={IconName.REFRESH} className="animate-spin text-[20px]! h-5 w-5" /> : <Icon name={IconName.GROUP} className="text-[20px]!" />}<button data-testid="change-account-link" className="text-gray-400 hover:text-gray-300 text-xs ml-2" type="button" onClick={onAccountModalOpenClick}>{dict.userProfile.menu.changeAccount}</button></p> : null}</div>{navigationItems.map(_temp)}</MenuItems></Transition></div>}</>;
        }}</Menu>;
      $[7] = accountModalOpenLoading;
      $[8] = companyUsers;
      $[9] = defaultBusinessUnit;
      $[10] = dict;
      $[11] = navigationItems;
      $[12] = onAccountModalOpenClick;
      $[13] = selectedPricingReference;
      $[14] = user.user.customer.email;
      $[15] = user.user.customer.firstName;
      $[16] = user.user.customer.lastName;
      $[17] = t4;
    } else {
      t4 = $[17];
    }
    return t4;
  }
  if (user && [UserStatus.UNREGISTERED].includes(user.userStatus)) {
    let t2;
    if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <span className="w-12 h-12 inline-flex justify-center items-center"><Icon name={IconName.PERSON} className="text-primary" data-testid="button-initials" /></span>;
      $[18] = t2;
    } else {
      t2 = $[18];
    }
    let t3;
    if ($[19] !== dict.general.logoutButton) {
      t3 = <a className="w-fit bg-black text-white flex flex-row items-stretch h-full no-underline! uppercase" href="/auth/logout" data-testid="user-profile-button">{t2}<span className="px-3 bg-gray-600 inline-flex justify-center items-center text-sm font-semibold" data-testid="button-text">{dict.general.logoutButton}</span></a>;
      $[19] = dict.general.logoutButton;
      $[20] = t3;
    } else {
      t3 = $[20];
    }
    return t3;
  }
  let t2;
  if ($[21] !== dict || $[22] !== lang || $[23] !== pathname) {
    t2 = <UserProfileButtonLoggedOut returnTo={pathname} lang={lang} dict={dict} />;
    $[21] = dict;
    $[22] = lang;
    $[23] = pathname;
    $[24] = t2;
  } else {
    t2 = $[24];
  }
  return t2;
}
function _temp(item) {
  return <MenuItem key={item.url} as={Link} href={item.url} prefetch={item.prefetch} data-testid={item.testId} className="px-4 py-2 text-sm no-underline! flex flex-row items-center ui-active:bg-gray-800"><MenuItemContent item={item} /></MenuItem>;
}